import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { criticalOrderRefs } from '@d-orca/shared/client/logistics';
import { useNow } from '@hooks/useNow';
import { ChevronRightIcon, CloseIcon } from '@utils/icons';
import type { Delivery, InventoryLevel, LogisticsKpis, UntrackedMaterial } from '@interfaces/ILogistics';
import { buildLogisticsAlertGroups, groupCount, groupQuery, namesQuery } from '../logisticsAlertGroups';
import { unsyncedMaterials, unsyncedTone, type KpiTone } from './kpiDerivations';
import { KpiFilterButton } from './KpiFilterButton';
import styles from './KpiCards.module.css';

interface IKpiCard {
  key: string;
  /** Plain text, or the tile's own filter control. */
  label: ReactNode;
  value: string;
  /** Severity in the tile's own form: the number is tinted, the card is not. */
  tone?: KpiTone;
  sub?: ReactNode;
  /** The page tour's anchor, for the one tile a step spotlights. */
  tour?: string;
  /** The Materials search the WHOLE tile applies when clicked. */
  filter?: string;
  /** That search names orders rather than materials. */
  byOrder?: boolean;
}

interface IKpiCardsProps {
  kpis: LogisticsKpis;
  /** The rows the alerts tile counts. It derives its own figure rather than
   *  reading `kpis.stockAlerts`, which counts only materials past their reorder
   *  point — one of the four conditions the alerts card raises, and the one that
   *  is forced false the moment a material actually runs out. */
  inventory: InventoryLevel[];
  /** Materials with an order but no stock record — the `stock-untracked` group.
   *  They carry no level, so they cannot be read off `inventory`. */
  untracked: UntrackedMaterial[];
  /** The full order list, for Critical orders and the alerts tile's Delivery late (open orders
   *  past it). */
  deliveries: Delivery[];
  /** The SITE's zone. Which calendar day it is there decides whether a delivery
   *  is late and whether a row is past its reorder point. */
  tz: string;
  /** The live Materials search, so a reason already filtering the panels can
   *  render as pressed and clear itself on a second click. */
  materialsQuery: string;
  onFilterMaterials: (query: string) => void;
}

export function KpiCards({
  kpis,
  inventory,
  untracked,
  deliveries,
  tz,
  materialsQuery,
  onFilterMaterials,
}: IKpiCardsProps) {
  const { t } = useTranslation();
  // Ticks, so lateness and the on-time window roll over at the site's
  // midnight on a page left open overnight.
  const now = useNow(60_000);
  // The locale's list comma — '、' in Japanese — glued to the item before it;
  // the flex gap supplies any space after it.
  const listSeparator = t('common.listSeparator');
  // Open Deliveries derives from the delivery list server-side; the other
  // three derive HERE from the real rows (2026-07-27 inventory spec). Gate
  // occupancy and projected spend used to sit in slots 3 and 4 as badged
  // fixtures awaiting a Trusstor and a Finance source; ADR-0012 gave the
  // slots to Critical orders and Delivered, not synced instead.
  //
  // The alerts tile counts the same four groups the dashboard's System Alerts card
  // raises — stock gap, ran out, delivery late, not tracked — off the same note
  // chain. It used to count only rows past their reorder point, and its
  // sub-line re-ran that same filter, so it could only ever restate the number
  // above it: "2 / 2 overdue".
  // Except Delivery late, which counts the overdue ORDERS — see the builder.
  const groups = buildLogisticsAlertGroups(inventory, untracked, deliveries, tz, now);
  const total = groups.reduce((sum, group) => sum + groupCount(group), 0);
  // Severity in the tile's own form, not a second tile. The number is tinted,
  // the card is NOT — a full border turns one tile into a boxed alarm and reads
  // as "this card is broken" rather than "one material needs ordering".
  const alertTone = groups[0]?.severity === 'critical' ? 'urgent' : groups.length > 0 ? 'warn' : undefined;

  /* Each reason filters the panels below to its own materials — the same
     quoted-name query the alerts card's "View in Logistics ›" link carries, so
     both routes land on exactly the same rows. Clicking the reason that is
     already filtering clears it: a filter with no way back from the control
     that set it is a trap. */
  const reasons: ReactNode = groups.length === 0 ? null : (
    <div className={styles.reasons}>
      {groups.map((group, index) => {
        const query = groupQuery(group);
        return (
          <span key={group.type} className={styles.reason}>
            <KpiFilterButton
              query={query}
              materialsQuery={materialsQuery}
              onFilterMaterials={onFilterMaterials}
              severity={group.severity}
              byOrder={group.refs !== undefined}
              className={styles.reasonButton}
            >
              {groupCount(group)}
              {' '}
              {/* The alerts card's own type labels, read from its keys rather
                  than copied — the tile says "Ran out" because the card does,
                  and a re-wording there reaches here for free. */}
              {t(`dashboard.activityAlerts.types.${group.type}`)}
            </KpiFilterButton>
            {/* A comma, glued to the reason before it. */}
            {index < groups.length - 1 ? (
              <span className={styles.reasonSep} aria-hidden="true">{listSeparator.trim()}</span>
            ) : null}
          </span>
        );
      })}
    </div>
  );

  // The WHOLE tile is the filter: click it and the panels below show just the
  // materials whose stock is stale — the same quoted query and the same
  // clear-on-second-click as an alerts reason.
  const unsynced = unsyncedMaterials(inventory, untracked);
  const critical = criticalOrderRefs(deliveries);

  const cards: IKpiCard[] = [
    { key: 'activeDeliveries', label: t('logistics.kpi.activeDeliveries'), value: String(kpis.activeDeliveries) },
    { key: 'stockAlerts', label: t('logistics.kpi.stockAlerts'), value: String(total), tone: alertTone, sub: reasons, tour: 'logistics-alerts' },
    {
      key: 'critical',
      label: t('logistics.kpi.criticalOrders'),
      value: String(critical.length),
      ...(critical.length > 0 ? { tone: 'warn' as const, filter: namesQuery(critical), byOrder: true } : {}),
    },
    {
      key: 'unsynced',
      label: t('logistics.kpi.unsyncedArrivals'),
      value: String(unsynced.length),
      tone: unsyncedTone(unsynced.length),
      ...(unsynced.length > 0 ? { filter: namesQuery(unsynced) } : {}),
    },
  ];
  return (
    <div className={styles.grid} data-tour="logistics-kpis">
      {cards.map((card) => (
        <article key={card.key} className={styles.card} data-tone={card.tone} data-tour={card.tour}>
          {card.filter === undefined ? (
            <>
              <div className={styles.value}>{card.value}</div>
              <div className={styles.label}>{card.label}</div>
              {card.sub ?? null}
            </>
          ) : (
            <KpiFilterButton
              query={card.filter}
              materialsQuery={materialsQuery}
              onFilterMaterials={onFilterMaterials}
              byOrder={card.byOrder}
              className={styles.cardButton}
            >
              <span className={styles.value}>{card.value}</span>
              <span className={styles.label}>{card.label}</span>
              {/* What a click does: filter, or clear while this tile is the filter. */}
              <span className={styles.cardGo} aria-hidden="true"><ChevronRightIcon size={18} /></span>
              <span className={styles.cardClear} aria-hidden="true"><CloseIcon size={18} /></span>
            </KpiFilterButton>
          )}
        </article>
      ))}
    </div>
  );
}
